import type { ReactNode } from "react";
import { ProductIcon } from "@/components/ProductIcon";
import { ChevronRightIcon, DownloadIcon, ExternalLinkIcon, InfoIcon } from "@/components/report/icons";

export function ReportSection({
  children,
  testId,
}: {
  children: ReactNode;
  testId?: string;
}) {
  return (
    <div
      className="rounded-xl border border-ink-10 overflow-hidden print-product-section"
      data-testid={testId}
    >
      {children}
    </div>
  );
}

export function ReportSectionHeader({
  title,
  subtitle,
  icon,
  platformIcons,
  expanded,
  onToggle,
  toggleTestId,
  showToggle = true,
  expandLabel,
  collapseLabel,
}: {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  platformIcons?: string[];
  expanded: boolean;
  onToggle: () => void;
  toggleTestId?: string;
  showToggle?: boolean;
  expandLabel: string;
  collapseLabel: string;
}) {
  return (
    <div className="px-5 py-4 border-b border-ink-10 flex items-center gap-3">
      {icon}
      <div className="flex-grow min-w-0">
        <h3 className="font-semibold text-base text-foreground">{title}</h3>
        {subtitle ? (
          <p className="text-[11px] text-muted-foreground/50 mt-0.5 uppercase tracking-widest font-medium">
            {subtitle}
          </p>
        ) : null}
      </div>
      {platformIcons?.map((name) => (
        <ProductIcon key={name} name={name} className="w-5 h-5 opacity-40 shrink-0" />
      ))}
      {showToggle ? (
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-1.5 cursor-pointer shrink-0 group/toggle"
          data-testid={toggleTestId}
          aria-expanded={expanded}
        >
          <span className="text-[11px] text-muted-foreground/60 group-hover/toggle:text-muted-foreground transition-colors">
            {expanded ? collapseLabel : expandLabel}
          </span>
          <div className={`transition-transform duration-200 ${expanded ? "rotate-90" : ""}`}>
            <ChevronRightIcon className="w-3.5 h-3.5 text-muted-foreground/60" />
          </div>
        </button>
      ) : null}
    </div>
  );
}

export function StepCard({
  number,
  title,
  expanded,
  maxHeightClass = "max-h-40",
  leading,
  trailing,
  children,
  testId,
}: {
  number?: number | string;
  title: ReactNode;
  expanded: boolean;
  maxHeightClass?: string;
  leading?: ReactNode;
  trailing?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <div
      className="group relative rounded-lg border border-ink-10 hover:border-ink-25 transition-all duration-200"
      data-testid={testId}
    >
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer">
        {leading ?? (
          <div className="w-6 h-6 rounded-full bg-ink-10 text-ink-70 flex items-center justify-center shrink-0 text-xs font-bold">
            {number}
          </div>
        )}
        <p className="font-medium text-sm text-muted-foreground flex-grow flex items-center gap-2 min-w-0">
          {title}
        </p>
        {trailing}
        <ChevronRightIcon className="w-3.5 h-3.5 text-muted-foreground group-hover:text-ink-70 transition-colors shrink-0" />
      </div>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          {
            "max-h-24": expanded ? "max-h-24" : "max-h-0 group-hover:max-h-24",
            "max-h-40": expanded ? "max-h-40" : "max-h-0 group-hover:max-h-40",
            "max-h-52": expanded ? "max-h-52" : "max-h-0 group-hover:max-h-52",
            "max-h-64": expanded ? "max-h-64" : "max-h-0 group-hover:max-h-64",
            "max-h-72": expanded ? "max-h-72" : "max-h-0 group-hover:max-h-72",
            "max-h-96": expanded ? "max-h-96" : "max-h-0 group-hover:max-h-96",
          }[maxHeightClass] ?? (expanded ? "max-h-40" : "max-h-0 group-hover:max-h-40")
        }`}
      >
        <div className="px-4 pb-3 ml-9 space-y-2">{children}</div>
      </div>
    </div>
  );
}

export function StepBody({ children }: { children: ReactNode }) {
  return <p className="text-[13px] text-foreground/60 leading-relaxed">{children}</p>;
}

export function ReportLink({
  href,
  children,
  testId,
  download,
  className,
}: {
  href: string;
  children: ReactNode;
  testId?: string;
  download?: boolean;
  className?: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={
        className ??
        "inline-flex items-center gap-1.5 mt-2 text-xs font-medium text-ink-80 hover:text-ink transition-colors"
      }
      data-testid={testId}
    >
      {download ? <DownloadIcon /> : null}
      {children}
      {download ? null : <ExternalLinkIcon />}
    </a>
  );
}

export function AmberAlert({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-400/20 bg-amber-400/5 px-3 py-2">
      <span className="mt-0.5 shrink-0 text-amber-300/80">
        <InfoIcon />
      </span>
      <div className="space-y-1 min-w-0">
        {title ? (
          <p className="text-[12px] text-amber-300/90 font-semibold leading-relaxed">{title}</p>
        ) : null}
        <div className="text-[12px] text-amber-300/75 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

export function NordicPackNotice({
  title,
  body,
  linkLabel,
  href,
  testId,
}: {
  title: string;
  body: string;
  linkLabel: string;
  href: string;
  testId: string;
}) {
  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.08] px-4 py-3 mt-1">
      <div className="space-y-1 min-w-0">
        <p className="text-[12px] font-semibold text-amber-300/95">{title}</p>
        <p className="text-[11px] text-amber-300/70 leading-relaxed">{body}</p>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 mt-0.5 text-xs font-medium text-amber-300 hover:text-amber-200 transition-colors"
          data-testid={testId}
        >
          <DownloadIcon />
          {linkLabel}
        </a>
      </div>
    </div>
  );
}

export function OptionalBadge({ label }: { label: string }) {
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded bg-ink-5 text-ink-40 border border-ink-10 shrink-0">
      {label}
    </span>
  );
}
